import { toaster } from "evergreen-ui";
import { useEffect, useRef, useState } from "react";
import { TundraStorefront } from "../../../../common/models";
import { CenteredSpinner } from "../../common/CenteredSpinner";
import {
    deleteTundraStorefront,
    useCreateCustomDomainMutation,
    useCreateStorefrontMutation,
    useGetStorefrontQuery,
    useValidateCustomDomainMutation
} from "../../datastore/TundraStorefrontDataStore";
import { StoreTundraAdminCreateView } from "./StoreTundraAdminCreateView";
import { StoreTundraAdminGetView } from "./StoreTundraAdminGetView";
import { useStoreEditDetailsContext } from "./StoreEditDetailsContext";

export const resolveRenderedStorefront = (
    hasPolled: boolean,
    fetched: TundraStorefront | null,
    validated: TundraStorefront | null | undefined,
    created: TundraStorefront | null | undefined
): TundraStorefront | null => {
    return (hasPolled ? fetched : null) ?? validated ?? created ?? fetched;
};

export const handlePollTick = (
    inFlightRef: { current: boolean },
    refetch: () => Promise<boolean>,
    markPolled: () => void
): void => {
    if (inFlightRef.current) {
        return;
    }
    inFlightRef.current = true;
    refetch()
        .then((succeeded) => {
            if (succeeded) {
                markPolled();
            }
        })
        .finally(() => {
            inFlightRef.current = false;
        });
};

export const StoreTundraAdminPage = () => {

    const {
        store
    } = useStoreEditDetailsContext();

    const {
        data: getStorefrontData,
        loading: getStorefrontLoading,
        refetch: refetchStorefront
    } = useGetStorefrontQuery(store?.domain ?? "");

    const [
        createCustomDomain,
        {
            loading: createCustomDomainLoading,
            called: createCustomDomainCalled,
            data: createdCustomDomainData
        }
    ] = useCreateCustomDomainMutation();

    const [
        validateCustomDomain,
        {
            loading: validateCustomDomainLoading,
            data: validatedCustomDomainData
        }
    ] = useValidateCustomDomainMutation();

    const [
        createStorefront,
        {
            loading: createStorefrontLoading,
            called: createStorefrontCalled,
            data: createdStorefrontData
        }
    ] = useCreateStorefrontMutation();

    const [
        deletingStorefront,
        setDeletingStorefront
    ] = useState<boolean>(false);

    const [hasPolled, setHasPolled] = useState<boolean>(false);
    useEffect(() => {
        setHasPolled(false);
    }, [validatedCustomDomainData, createdCustomDomainData]);

    const renderedStorefront = resolveRenderedStorefront(
        hasPolled, getStorefrontData, validatedCustomDomainData, createdCustomDomainData
    );
    const hasDeployingCert: boolean = !!renderedStorefront?.domains?.some(
        (domain) => domain.alias.lastCertificateRenewal === "DEPLOYING"
    );

    const pollInFlightRef = useRef<boolean>(false);
    useEffect(() => {
        if (!hasDeployingCert) {
            return;
        }
        const intervalId = setInterval(() => {
            handlePollTick(pollInFlightRef, refetchStorefront, () => setHasPolled(true));
        }, 15000);
        return () => clearInterval(intervalId);
    }, [hasDeployingCert, refetchStorefront]);

    const deleteStorefront = async (domain: string): Promise<void> => {
        if (!window.confirm("Are you sure you want to delete this storefront? It will take some time to recreate it.")) {
            return; // Don't delete if the user selects cancel.
        }
        setDeletingStorefront(true);
        if (await deleteTundraStorefront(domain)) {
            window.location.reload(); // Let's just refresh this
        } else {
            toaster.danger("Failed to delete storefront - check logs for more information.");
        }
        setDeletingStorefront(false);
    };

    const renderStorefrontView = (storefront: TundraStorefront): JSX.Element => {
        return <StoreTundraAdminGetView
            storefront={storefront}
            createCustomDomain={createCustomDomain}
            createCustomDomainLoading={createCustomDomainLoading}
            createCustomDomainCalled={createCustomDomainCalled}
            validateCustomDomain={validateCustomDomain}
            validateCustomDomainLoading={validateCustomDomainLoading}
            deleteStorefront={deleteStorefront}
        />;
    };

    const renderCreateStorefrontView = (): JSX.Element => {
        return <StoreTundraAdminCreateView
            createStorefront={createStorefront}
            createStorefrontLoading={createStorefrontLoading}
            storeId={store!.id}
        />;
    };

    // Loading is also true on poll refetches; only spin before the first load.
    if ((getStorefrontLoading && !getStorefrontData) || deletingStorefront) {
        return <CenteredSpinner />;
    }

    if (createStorefrontCalled && !createStorefrontLoading && !createdStorefrontData) {
        toaster.danger("Failed to create storefront - check inputs and try again.");
    }

    if (createdCustomDomainData && getStorefrontData &&
        createdCustomDomainData.domains.length === getStorefrontData.domains.length) {
        toaster.danger("Failed to create custom domain - check logs for more information.");
    }

    if (renderedStorefront) {
        return renderStorefrontView(renderedStorefront);
    }

    // Newly created storefront
    if (createdStorefrontData) {
        return renderStorefrontView(createdStorefrontData);
    }

    // Create storefront view
    return renderCreateStorefrontView();
};
